import { cn } from "cn";
import { Button } from "@/components/ui/button";
import { BwCloseLine, BwRefreshLoadLeftLine } from "@/components/ui/icons";
import { useAppVersionStore } from "./use-app-version-store";

/**
 * Passive notice that a newer build is live, as a fixed card in the bottom-left
 * of the content area.
 *
 * It is deliberately not a dialog: no scrim, no focus trap, no dialog
 * semantics, so the user keeps working and never has focus pulled out of what
 * they are typing. `role="status"` announces it once instead.
 *
 * Dismissing only hides the card — `hideNotice` leaves `isUpdatePending` armed,
 * so the update still applies on the next navigation.
 */
export function AppUpdateModal() {
  const isNoticeVisible = useAppVersionStore((state) => state.isNoticeVisible);

  const hideNotice = useAppVersionStore((state) => state.actions.hideNotice);

  if (!isNoticeVisible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "fixed right-4 bottom-4 left-4 z-40",
        "flex flex-col",
        "overflow-hidden",
        "border border-border-dark-low rounded-3xl bg-bg-surface-elevate-low shadow-lg",
        // pointer-events-auto keeps the notice clickable even when a blocking
        // dialog sets `pointer-events: none` on the document body.
        "pointer-events-auto",
        "animate-in fade-in-0 slide-in-from-bottom-2 duration-200",
        // Offset past the sidebar, which only mounts in flow at >= 1280px.
        "sm:right-auto sm:left-4 sm:w-80",
        "xl:left-[calc(var(--bw-sidebar-width,0px)+16px)]",
      )}
    >
      <Button
        size="icon-md"
        kind="soft"
        variant="neutral"
        aria-label="Dismiss"
        onClick={hideNotice}
        className="absolute top-3 right-3"
      >
        <BwCloseLine size={20} />
      </Button>
      <div className="flex flex-col gap-4 p-4">
        <BwRefreshLoadLeftLine
          size={40}
          className="shrink-0 text-text-primary"
        />
        <div className="flex flex-col">
          <p className="text-base font-medium text-text-dark-max">
            New version available
          </p>
          <p className="text-sm text-text-dark-high">
            Refresh when you reach a good stopping point. Nothing in progress is
            lost.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 p-3">
        <Button
          kind="outline"
          variant="neutral"
          className="flex-1"
          onClick={hideNotice}
        >
          Not now
        </Button>
        <Button
          className="flex-1"
          onClick={() => {
            window.location.reload();
          }}
        >
          Refresh now
        </Button>
      </div>
    </div>
  );
}
